import React, { useEffect, useState } from 'react';
import { Edit, Save, Trash, X } from 'react-feather';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { Typography } from '@/UIlibrary/Typography/Typography';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import Button from '@/UIlibrary/Button/Button';
import { SCHEMAS } from '@/apolloClient';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import {
	useModifyDeliberationMutation,
	ModifyDeliberationInput,
	ModifyDeliberationMutation,
	Registry
} from '@/graphql/__generated__/onboarding';
import logger from '@/logger';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import CreditBureauSummary from './CreditBureauSummary';

interface DeliberationDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	t: any;
	clientId: string;
	deliberation: string;
	isResizing: boolean;
	drawerWidth: number;
	onResizingChange: (isResizing: boolean) => void;
	onDrawerWidthChange: (width: number) => void;
	onSuccess?: () => void;
	registry: Registry;
}

const DeliberationDrawer: React.FC<DeliberationDrawerProps> = ({
	isOpen,
	onClose,
	clientId,
	t,
	drawerWidth,
	onDrawerWidthChange,
	isResizing,
	onResizingChange,
	deliberation,
	onSuccess,
	registry
}) => {
	const drawerRef = React.useRef<HTMLDivElement>(null);
	const [updatedDeliberation, setUpdatedDeliberation] = useState<string>(deliberation);
	const [error, setError] = useState<string>('');
	const [myClientId, setClientId] = useState<string>(clientId);

	const [isEditing, setIsEditing] = useState(false);
	const onboardingClient = useApolloClient(SCHEMAS.ONBOARDING);

	const [modifyDeliberation, { loading }] = useModifyDeliberationMutation({
		client: onboardingClient,
		onCompleted: async (data: ModifyDeliberationMutation) => {
			const modifyResult = data.modifyDeliberation;
			if (modifyResult?.successful) {
				setIsEditing(false);
				setError('');
				logger.success('Deliberation modified successfully', modifyResult);
				toast.success(t('DeliberationModifiedSuccessfully'));
				onClose();
				if (onSuccess) {
					onSuccess();
				}
			}
			if (modifyResult?.mutationErrors && modifyResult.mutationErrors.length > 0) {
				let hasFieldErrors = false;

				modifyResult.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								if (error.field === 'deliberation') {
									setError(error.message || t('DeliberationRequired'));
									hasFieldErrors = true;
								}
								break;
							case 'ActionFailed':
								logger.error('Action failed', error.message);
								break;
							case 'InvalidAction':
								logger.error('Invalid action', error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});

				if (!hasFieldErrors) {
					toast.error(t('ErrorModifyingDeliberation'));
				}
			}
		},
		onError: (error) => {
			logger.error('Error Modifying Deliberation', error);
			toast.error(t('ErrorModifyingDeliberation'));
		}
	});

	// Set client ID
	useEffect(() => {
		if (clientId) {
			setClientId(clientId);
		}
		if (deliberation) {
			setUpdatedDeliberation(deliberation);
		}
	}, [clientId, deliberation]);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isResizing) return;

			const newWidth = window.innerWidth - e.clientX;
			if (newWidth > 200 && newWidth < 600) {
				onDrawerWidthChange(newWidth);
			}
		};

		const handleMouseUp = () => {
			onResizingChange(false);
		};

		if (isResizing) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		}

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isResizing, onDrawerWidthChange, onResizingChange]);

	const handleUpdateDeliberation = async () => {
		if (!updatedDeliberation.trim()) {
			setError(t('DeliberationRequired'));
			return;
		}

		const input: ModifyDeliberationInput = {
			clientId: myClientId,
			deliberation: updatedDeliberation
		};
		await modifyDeliberation({
			variables: {
				input
			}
		});
	};

	const handleCancelEdit = () => {
		setUpdatedDeliberation(deliberation);
		setIsEditing(false);
	};

	const deliberationLength = t('DeliberationLength') + '. ' + updatedDeliberation.length + '/3400';

	if (!isOpen) return null;

	return (
		<div
			ref={drawerRef}
			className="h-full bg-white fixed right-0 top-0 bottom-0 "
			style={{
				width: `${drawerWidth}px`,
				height: '100vh',
				zIndex: 99
			}}
		>
			<div className="flex flex-col h-full">
				<div
					className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize group flex items-center"
					onMouseDown={() => onResizingChange(true)}
				>
					<div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-200 group-hover:bg-brand-600 transition-colors duration-200" />
					<div
						className="absolute left-0 w-4 h-8 -ml-2 top-1/2 -translate-y-1/2 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"
						style={{ backgroundColor: 'rgba(30, 96, 145, 0.1)' }}
					>
						<div className="h-full w-full flex items-center justify-center">
							<div className="w-0.5 h-4 bg-brand-600 mx-0.5" />
							<div className="w-0.5 h-4 bg-brand-600 mx-0.5" />
						</div>
					</div>
				</div>

				<div className="flex-none p-4 border-b bg-white sticky top-0 z-10">
					<div className="flex justify-between items-center">
						<Typography size="lg" weight="semibold">
							{t('ViewDeliberation')}
						</Typography>
						<button
							onClick={onClose}
							className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
						>
							<X size={24} color={BRAND_COLORS.brand600} />
						</button>
					</div>
				</div>

				<div className="flex-1 overflow-y-auto">
					<div className="flex flex-col px-3 mt-5 gap-4">
						{isEditing ? (
							<div className="flex w-full flex-row">
								<div className="flex w-1/2 justify-center">
									<Button
										label={t('DiscardChanges')}
										onClick={handleCancelEdit}
										size="auto"
										endIcon={<Trash size={20} />}
										color="error"
										loading={loading}
									/>
								</div>
								<div className="flex w-1/2 justify-center">
									<Button
										label={t('SaveChanges')}
										onClick={handleUpdateDeliberation}
										size="auto"
										endIcon={<Save size={20} />}
										loading={loading}
									/>
								</div>
							</div>
						) : (
							<div className="flex w-full justify-end">
								<div className="w-1/2">
									<Button
										label={t('UpdateDeliberation')}
										onClick={() => setIsEditing(true)}
										size="auto"
										endIcon={<Edit size={20} />}
									/>
								</div>
							</div>
						)}
						<CreditBureauSummary registry={registry} />
						<TextArea
							value={updatedDeliberation !== null ? updatedDeliberation : deliberation || ''}
							rows={24}
							onChange={(prev) => {
								setError('');
								setUpdatedDeliberation(prev.target.value);
							}}
							disabled={!isEditing || loading}
							error={!!error}
							hint={error || deliberationLength}
							maxChars={3400}
							strictMaxChars
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DeliberationDrawer;
